import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL } from "../lib/format";
import { Skeleton } from "./ui/skeleton";
import { AiYorum } from "./AiYorum";
import { ChevronDown, ChevronRight, Table2 } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const CEYREK_AYLARI = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };
const CEYREK_ETIKET = { 1: "1. Çeyrek • Ocak–Mart", 2: "2. Çeyrek • Nisan–Haziran", 3: "3. Çeyrek • Temmuz–Eylül", 4: "4. Çeyrek • Ekim–Aralık" };

/* Fark hücresi: Bütçe − Fiili. Negatif = aşım (kırmızı, parantezli), pozitif = tasarruf (yeşil). */
const FarkHucre = ({ butce, fiili }) => {
  if (!butce && !fiili) return <span className="text-muted-foreground/50">—</span>;
  const fark = butce - fiili;
  if (Math.abs(fark) < 0.5) return <span className="text-muted-foreground/50">—</span>;
  if (fark < 0) {
    return <span className="font-semibold text-red-600">({formatTL(Math.abs(fark))})</span>;
  }
  return <span className="font-semibold text-emerald-600">{formatTL(fark)}</span>;
};

const TutarHucre = ({ deger, koyulastir = false }) => {
  if (!deger) return <span className="text-muted-foreground/50">—</span>;
  return <span className={koyulastir ? "font-semibold" : ""}>{formatTL(deger)}</span>;
};

export const ButceRapor = ({ yil = 2026 }) => {
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gorunum, setGorunum] = useState("1"); // "1".."4" | "yillik"
  const [kapali, setKapali] = useState(() => new Set());

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/butce/rapor", { params: { yil } });
      setRapor(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Bütçe raporu yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil]);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  const toggleKategori = (kat) => {
    setKapali((prev) => {
      const yeni = new Set(prev);
      if (yeni.has(kat)) yeni.delete(kat);
      else yeni.add(kat);
      return yeni;
    });
  };

  // Kategori sırasına göre grupla
  const gruplar = useMemo(() => {
    if (!rapor) return [];
    const map = new Map();
    rapor.kalemler.forEach((k) => {
      if (!map.has(k.ana_kategori)) map.set(k.ana_kategori, []);
      map.get(k.ana_kategori).push(k);
    });
    return Array.from(map.entries()).map(([kategori, kalemler]) => ({ kategori, kalemler }));
  }, [rapor]);

  // Görünüme göre sütunlar: [{tip:"ay", ay:1}, ...] veya [{tip:"ceyrek", c:1}, ...]
  const sutunlar = useMemo(() => {
    if (gorunum === "yillik") {
      return [1, 2, 3, 4].map((c) => ({ tip: "ceyrek", key: `c${c}`, etiket: `${c}. Çeyrek`, aylar: CEYREK_AYLARI[c] }));
    }
    return CEYREK_AYLARI[parseInt(gorunum)].map((m) => ({ tip: "ay", key: `a${m}`, etiket: `${AYLAR[m - 1]} ${yil}`, aylar: [m] }));
  }, [gorunum, yil]);

  const topla = (dizi, aylar) => aylar.reduce((acc, m) => acc + (dizi[m - 1] || 0), 0);

  // Bir kalem/kategori satırı için tüm sütun değerleri + dönem toplamı
  const satirDegerleri = (butceDizi, fiiliDizi) => {
    const hucreler = sutunlar.map((s) => ({
      butce: topla(butceDizi, s.aylar),
      fiili: topla(fiiliDizi, s.aylar),
    }));
    const tumAylar = sutunlar.flatMap((s) => s.aylar);
    const toplam = { butce: topla(butceDizi, tumAylar), fiili: topla(fiiliDizi, tumAylar) };
    return { hucreler, toplam };
  };

  // Kategori toplam dizileri (12 ay)
  const kategoriDizileri = (kalemler) => {
    const butce = Array(12).fill(0);
    const fiili = Array(12).fill(0);
    kalemler.forEach((k) => {
      for (let i = 0; i < 12; i++) {
        butce[i] += k.aylik_butce[i] || 0;
        fiili[i] += k.gerceklesen[i] || 0;
      }
    });
    return { butce, fiili };
  };

  // Genel toplam dizileri
  const genel = useMemo(() => {
    const butce = Array(12).fill(0);
    const fiili = Array(12).fill(0);
    (rapor?.kalemler || []).forEach((k) => {
      for (let i = 0; i < 12; i++) {
        butce[i] += k.aylik_butce[i] || 0;
        fiili[i] += k.gerceklesen[i] || 0;
      }
    });
    return { butce, fiili };
  }, [rapor]);

  if (loading) {
    return (
      <div className="space-y-3" data-testid="butce-rapor-loading">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!rapor) return null;

  const donemEtiketi = gorunum === "yillik" ? `Yıllık Özet ${yil}` : CEYREK_ETIKET[parseInt(gorunum)];
  const toplamEtiketi = gorunum === "yillik" ? "Yıl Toplamı" : "Çeyrek Toplamı";
  const genelDegerler = satirDegerleri(genel.butce, genel.fiili);

  return (
    <div className="space-y-4">
      {/* Başlık + görünüm seçimi */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Table2 className="h-5 w-5 text-[hsl(var(--accent))]" />
            {yil} Bütçe &amp; Gerçekleşen Gider Yönetimi
          </h2>
          <p className="text-xs text-muted-foreground">
            Yönetim bakışı • TL • Bütçe / Fiili / Fark — negatif fark (kırmızı) bütçe aşımıdır
          </p>
        </div>
        <div className="ml-auto flex items-center gap-1 overflow-x-auto rounded-lg border bg-card p-1" data-testid="butce-rapor-gorunum">
          {["1", "2", "3", "4", "yillik"].map((g) => (
            <button
              key={g}
              onClick={() => setGorunum(g)}
              data-testid={`butce-rapor-gorunum-${g}`}
              className={`shrink-0 rounded-md px-3 py-2 text-xs font-medium transition-colors min-h-[36px] ${
                gorunum === g
                  ? "bg-[hsl(var(--accent))] text-[hsl(var(--primary))] font-semibold"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {g === "yillik" ? "Yıllık Özet" : `${g}. Çeyrek`}
            </button>
          ))}
        </div>
      </div>

      {/* Excel tarzı tablo */}
      <div className="rounded-xl border bg-card overflow-hidden" data-testid="butce-rapor-tablo">
        <div className="border-b bg-[hsl(var(--primary))] px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--accent))]">{donemEtiketi}</span>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-muted/60 text-muted-foreground">
                <th rowSpan={2} className="sticky left-0 z-10 bg-muted/60 backdrop-blur px-3 py-2 text-left font-semibold uppercase tracking-wide min-w-[220px] border-r">
                  Gider Kalemi
                </th>
                {sutunlar.map((s) => (
                  <th key={s.key} colSpan={3} className="px-2 py-1.5 text-center font-semibold border-l border-border/60">
                    {s.etiket}
                  </th>
                ))}
                <th colSpan={3} className="px-2 py-1.5 text-center font-bold border-l-2 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 text-[hsl(var(--primary))]">
                  {toplamEtiketi}
                </th>
              </tr>
              <tr className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                {sutunlar.map((s) => (
                  <SutunAltBaslik key={s.key} vurgulu={false} />
                ))}
                <SutunAltBaslik vurgulu />
              </tr>
            </thead>
            <tbody>
              {gruplar.map(({ kategori, kalemler }) => {
                const dz = kategoriDizileri(kalemler);
                const kd = satirDegerleri(dz.butce, dz.fiili);
                const kapaliMi = kapali.has(kategori);
                return (
                  <KategoriGrubu
                    key={kategori}
                    kategori={kategori}
                    kalemler={kalemler}
                    kd={kd}
                    kapaliMi={kapaliMi}
                    onToggle={() => toggleKategori(kategori)}
                    satirDegerleri={satirDegerleri}
                  />
                );
              })}
              {/* GENEL TOPLAM */}
              <tr className="border-t-2 border-[hsl(var(--accent))] bg-[hsl(var(--primary))] text-white" data-testid="butce-rapor-genel-toplam">
                <td className="sticky left-0 z-10 bg-[hsl(var(--primary))] px-3 py-2.5 font-bold uppercase tracking-wide border-r border-white/10">
                  Genel Toplam
                </td>
                {genelDegerler.hucreler.map((h, i) => (
                  <HucreUclu key={i} butce={h.butce} fiili={h.fiili} koyu />
                ))}
                <HucreUclu butce={genelDegerler.toplam.butce} fiili={genelDegerler.toplam.fiili} koyu vurgulu />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Bütçe Analizi */}
      <AiYorum tip="butce" yil={yil} donem="yil" baslik="AI Bütçe Analizi" />
    </div>
  );
};

const SutunAltBaslik = ({ vurgulu }) => (
  <>
    <th className={`px-2 py-1 text-right font-medium border-l ${vurgulu ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10" : "border-border/60"} min-w-[86px]`}>Bütçe</th>
    <th className={`px-2 py-1 text-right font-medium ${vurgulu ? "bg-[hsl(var(--accent))]/10" : ""} min-w-[86px]`}>Fiili</th>
    <th className={`px-2 py-1 text-right font-medium ${vurgulu ? "bg-[hsl(var(--accent))]/10" : ""} min-w-[86px]`}>Fark</th>
  </>
);

const HucreUclu = ({ butce, fiili, koyu = false, vurgulu = false }) => (
  <>
    <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap border-l ${vurgulu ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5" : "border-border/40"}`}>
      <TutarHucre deger={butce} koyulastir={koyu} />
    </td>
    <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap ${vurgulu ? "bg-[hsl(var(--accent))]/5" : ""}`}>
      <TutarHucre deger={fiili} koyulastir={koyu} />
    </td>
    <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap ${vurgulu ? "bg-[hsl(var(--accent))]/5" : ""}`}>
      <FarkHucre butce={butce} fiili={fiili} />
    </td>
  </>
);

const KategoriGrubu = ({ kategori, kalemler, kd, kapaliMi, onToggle, satirDegerleri }) => {
  // "1-İnsan & Organizasyon" -> kod: "01", ad: "İnsan & Organizasyon"
  const [kod, ...adParcalari] = kategori.split("-");
  const ad = adParcalari.join("-");
  return (
    <>
      <tr
        className="cursor-pointer border-t bg-secondary/80 hover:bg-secondary transition-colors"
        onClick={onToggle}
        data-testid={`butce-rapor-kategori-${kod}`}
      >
        <td className="sticky left-0 z-10 bg-secondary px-3 py-2.5 border-r">
          <span className="flex items-center gap-1.5 font-bold text-[hsl(var(--primary))]">
            {kapaliMi ? <ChevronRight className="h-3.5 w-3.5 shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 shrink-0" />}
            <span className="text-[10px] text-muted-foreground font-semibold">{kod.padStart(2, "0")}</span>
            {ad}
          </span>
        </td>
        {kd.hucreler.map((h, i) => (
          <HucreUclu key={i} butce={h.butce} fiili={h.fiili} koyu />
        ))}
        <HucreUclu butce={kd.toplam.butce} fiili={kd.toplam.fiili} koyu vurgulu />
      </tr>
      {!kapaliMi &&
        kalemler.map((k) => {
          const sd = satirDegerleri(k.aylik_butce, k.gerceklesen);
          return (
            <tr key={k.id} className="border-t border-border/40 hover:bg-muted/30 transition-colors" data-testid="butce-rapor-kalem-satiri">
              <td className="sticky left-0 z-10 bg-card px-3 py-1.5 border-r">
                <span className="block pl-6 text-foreground/85 whitespace-nowrap">• {k.kalem_adi}</span>
              </td>
              {sd.hucreler.map((h, i) => (
                <HucreUclu key={i} butce={h.butce} fiili={h.fiili} />
              ))}
              <HucreUclu butce={sd.toplam.butce} fiili={sd.toplam.fiili} koyu vurgulu />
            </tr>
          );
        })}
    </>
  );
};
